import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  Button as BsButton,
  Card as BsCard,
  Form,
  Modal as BsModal,
} from "react-bootstrap";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useUserManagement } from "hooks";
import useFormattedDate from "hooks/useFormatDate";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import paths from "routes/paths";
import Avatar from "components/common/Avatar";
import CopyableText from "components/common/CopyableText";
import placeholderPhoto from "assets/img/profileplaceholder.png";

const STATUS_OPTIONS = [
  { value: "NONE", label: "None" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "VERIFIED", label: "Verified" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "TERMINATED", label: "Terminated" },
  { value: "DELETED", label: "Deleted" },
];

const STATUS_COLORS = {
  NONE: "default",
  PENDING: "gold",
  APPROVED: "green",
  VERIFIED: "green",
  SUSPENDED: "orange",
  CANCELLED: "red",
  TERMINATED: "red",
  DELETED: "red",
};

const toText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }
  return String(value);
};

const HelperProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { formatDateTime } = useFormattedDate();
  const { getHelperProfileByUserId, updateHelperProfileStatus } =
    useUserManagement();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [helperProfile, setHelperProfile] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("NONE");
  const [notes, setNotes] = useState("");
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const loadHelperProfile = useCallback(async ({ silent = false } = {}) => {
    if (!userId) return;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const profile = await getHelperProfileByUserId(userId);
      setHelperProfile(profile);
      setSelectedStatus(profile?.helperStatus || profile?.status || "NONE");
      setNotes(profile?.statusNotes || profile?.notes || "");
    } catch (err) {
      if (!silent) {
        setError(err?.message || "Failed to load helper profile");
        setHelperProfile(null);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [getHelperProfileByUserId, userId]);

  useEffect(() => {
    loadHelperProfile();
  }, [loadHelperProfile]);

  const currentStatus = helperProfile?.helperStatus || helperProfile?.status || "NONE";
  const currentNotes = helperProfile?.statusNotes || helperProfile?.notes || "";
  const statusChanged = selectedStatus !== currentStatus;
  const notesChanged = notes.trim() !== currentNotes.trim();

  const openStatusModal = useCallback(() => {
    setSelectedStatus(currentStatus);
    setNotes(currentNotes);
    setStatusModalOpen(true);
  }, [currentNotes, currentStatus]);

  const helperSkills = useMemo(() => {
    if (!Array.isArray(helperProfile?.helperSkills)) return [];

    return helperProfile.helperSkills
      .map((entry, index) => {
        if (!entry) return null;

        if (typeof entry === "string") {
          return {
            key: `skill-${index}`,
            name: entry,
          };
        }

        const nestedSkill = entry?.skill && typeof entry.skill === "object"
          ? entry.skill
          : null;
        const name = nestedSkill?.name || entry?.name || "";
        if (!name) return null;

        return {
          key: entry.id || nestedSkill?.id || `skill-${index}`,
          name,
          description: nestedSkill?.description || entry?.description || "",
          proficiencyLevel: entry?.proficiencyLevel || null,
          yearsOfExperience:
            typeof entry?.yearsOfExperience === "number"
              ? entry.yearsOfExperience
              : null,
          endorsed: entry?.endorsed === true,
          upVotes: entry?.upVotes === true,
        };
      })
      .filter(Boolean);
  }, [helperProfile]);

  const helperDetails = useMemo(
    () => [
      {
        label: "ID",
        value: <CopyableText value={helperProfile?.id} />,
      },
      {
        label: "Helper Status",
        value: (
          <Space size={8} align="center">
            <Tag color={STATUS_COLORS[currentStatus] || "default"}>
              {currentStatus}
            </Tag>
            <Guard privilege={PRIV.UPDATE_USERS}>
              <BsButton
                type="button"
                size="sm"
                variant="outline-success"
                className="text-xs"
                onClick={openStatusModal}
                aria-label="Edit helper status"
                title="Edit helper status"
              >
                <FontAwesomeIcon icon="edit" />
              </BsButton>
            </Guard>
          </Space>
        ),
      },
      {
        label: "Status Notes",
        value: toText(helperProfile?.statusNotes || helperProfile?.notes),
      },
      {
        label: "Preferred First Name",
        value: toText(helperProfile?.preferredFirstName),
      },
      {
        label: "Preferred Last Name",
        value: toText(helperProfile?.preferredLastName),
      },
      { label: "First Name", value: toText(helperProfile?.firstName) },
      { label: "Last Name", value: toText(helperProfile?.lastName) },
      { label: "Email", value: toText(helperProfile?.email) },
      { label: "Phone", value: toText(helperProfile?.phone) },
      { label: "Gender", value: toText(helperProfile?.gender) },
      { label: "Location", value: toText(helperProfile?.location) },
      { label: "Completed Tasks", value: helperProfile?.completedTasks ?? 0 },
      { label: "Average Rating", value: helperProfile?.averageRating ?? 0 },
      {
        label: "Signed Up At",
        value: formatDateTime(helperProfile?.signedUpAt),
      },
      {
        label: "Verified",
        value: helperProfile?.verified ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag>No</Tag>
        ),
      },
      
    ],
    [currentStatus, formatDateTime, helperProfile, openStatusModal],
  );

  const helperDisplayName = useMemo(() => {
    const firstName = helperProfile?.firstName || "";
    const lastName = helperProfile?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "this helper";
  }, [helperProfile?.firstName, helperProfile?.lastName]);

  const handleSaveStatus = async () => {
    if (!userId || !selectedStatus) return;
    try {
      setSaving(true);
      await updateHelperProfileStatus(userId, selectedStatus, notes || null);
      toast.success("Helper profile status updated");
      setStatusModalOpen(false);
      await loadHelperProfile({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Failed to update helper profile status");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "60vh" }}>
        <Spin size="default" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mt-3">
        <Alert type="error" showIcon message="Failed to load helper profile" description={error} />
        <Button
          icon={<ArrowLeftOutlined />}
          className="mt-3"
          onClick={() => navigate(paths.userDetails(userId))}
        >
          Back to User Details
        </Button>
      </Card>
    );
  }

  if (!helperProfile) {
    return (
      <Card className="mt-3">
        <Empty description="No helper profile found for this user." />
        <div className="mt-3 text-center">
          <Button onClick={() => navigate(paths.userDetails(userId))}>
            Back to User Details
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <BsCard className="mb-4 shadow-none mt-3 rounded border-1">
        <BsCard.Body className="d-flex flex-wrap align-items-center justify-content-between py-3 px-4">
          <div className="d-flex align-items-center mb-2 mb-sm-0">
            <BsButton
              type="button"
              onClick={() => navigate(-1)}
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </BsButton>
          </div>

          <h4 className="mb-2 mb-sm-0 fw-semibold text-base flex-grow-1 text-center ms-3">
            Helper Profile
          </h4>

          <div
            className="d-flex gap-2 flex-wrap justify-content-center justify-content-sm-end"
            style={{ minWidth: 180 }}
          >
            <Guard privilege={PRIV.VIEW_USERS}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate(paths.userDetails(userId))}
                className="text-nowrap text-xs"
              >
                View User Profile
              </Button>
            </Guard>
          </div>
        </BsCard.Body>
      </BsCard>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card title="Photos">
              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <Typography.Text
                    type="secondary"
                    className="text-xxs d-block mb-2 text-center"
                  >
                    Security Photo
                  </Typography.Text>
                  <div className="d-flex justify-content-center">
                    <Avatar
                      src={helperProfile.securityPhoto || placeholderPhoto}
                      size="5xl"
                    />
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <Typography.Text
                    type="secondary"
                    className="text-xxs d-block mb-2 text-center"
                  >
                    Profile Photo
                  </Typography.Text>
                  <div className="d-flex justify-content-center">
                    <Avatar
                      src={helperProfile.profilePhoto || placeholderPhoto}
                      size="5xl"
                    />
                  </div>
                </Col>
              </Row>
            </Card>

            <Card title="About Helper">
              <Typography.Text type="secondary" className="d-block">
                Description
              </Typography.Text>
              <Typography.Paragraph className="mb-3">
                {toText(helperProfile.description)}
              </Typography.Paragraph>

              <Typography.Text strong>Helper Skills</Typography.Text>
              <div className="mt-2">
                {helperSkills.length ? (
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {helperSkills.map((skill) => (
                      <div
                        key={skill.key}
                        className="border rounded p-2"
                        style={{ background: "#fafafa" }}
                      >
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                          <Tag color="blue" className="mb-0">
                            {skill.name}
                          </Tag>
                          {skill.proficiencyLevel && (
                            <Tag color="geekblue" className="mb-0">
                              {skill.proficiencyLevel}
                            </Tag>
                          )}
                          {typeof skill.yearsOfExperience === "number" && (
                            <Tag color="purple" className="mb-0">
                              {`${skill.yearsOfExperience} yrs`}
                            </Tag>
                          )}
                          {skill.endorsed && (
                            <Tag color="green" className="mb-0">
                              Endorsed
                            </Tag>
                          )}
                          {skill.upVotes && (
                            <Tag color="cyan" className="mb-0">
                              Upvoted
                            </Tag>
                          )}
                        </div>
                        {skill.description && (
                          <Typography.Text type="secondary" className="text-xxs d-block">
                            {skill.description}
                          </Typography.Text>
                        )}
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">No helper skills.</Typography.Text>
                )}
              </div>
            </Card>
          </Space>
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card title="Helper Details">
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {helperDetails.map((item) => (
                  <div key={item.label}>
                    <Typography.Text type="secondary" className="text-xxs d-block">
                      {item.label}
                    </Typography.Text>
                    {typeof item.value === "string" ||
                    typeof item.value === "number" ? (
                      <Typography.Text strong>{item.value}</Typography.Text>
                    ) : (
                      item.value
                    )}
                  </div>
                ))}
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>

      <Guard privilege={PRIV.UPDATE_USERS}>
        <BsModal
          show={statusModalOpen}
          onHide={() => setStatusModalOpen(false)}
          centered
          backdrop="static"
        >
          <BsModal.Header closeButton>
            <BsModal.Title className="text-sm">Update Helper Status</BsModal.Title>
          </BsModal.Header>
          <BsModal.Body>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Typography.Text type="secondary">
                Update helper status for {helperDisplayName}.
              </Typography.Text>
              <Form.Select
                size="sm"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                as="textarea"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional status notes"
                maxLength={2000}
              />
            </Space>
          </BsModal.Body>
          <BsModal.Footer className="border-0">
            <Button onClick={() => setStatusModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSaveStatus}
              disabled={(!statusChanged && !notesChanged) || saving}
            >
              Save Changes
            </Button>
          </BsModal.Footer>
        </BsModal>
      </Guard>
    </Space>
  );
};

export default HelperProfilePage;
